import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
} from "@material-ui/core";
import { useLayoutDispatch, popupNotification } from "context/LayoutContext";
import { receiveContainer } from "api/purchase";
import { receiptAvailableDocks } from "api/receipt";
import { getLabels } from "api/locations";
import { Printer } from "icons";

export default function ReceiveModal({ containerId, containerType, open, onClose }) {
  const layoutDispatch = useLayoutDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [docks, setDocks] = useState([]);
  const [dockId, setDockId] = useState("");

  const isAir = containerType === "Air";

  useEffect(() => {
    if (open && !isAir) {
      receiptAvailableDocks({
        responseSetter: (res) => setDocks(res?.data || []),
      });
    }
    if (!open) {
      setDockId("");
      setDocks([]);
    }
  }, [open, isAir]);

  const selectedDock = docks.find((d) => d.id === dockId);

  const printPDF = (base64PDF) => {
    try {
      const base64Data = base64PDF.replace(/^data:application\/pdf;base64,/, "");
      const byteCharacters = atob(base64Data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: "application/pdf" });
      const blobUrl = URL.createObjectURL(blob);

      const iframe = document.createElement("iframe");
      iframe.style.display = "none";
      document.body.appendChild(iframe);
      iframe.src = blobUrl;
      iframe.onload = () => {
        try {
          iframe.contentWindow.print();
        } catch (error) {
          console.error(error);
        }
      };
    } catch (error) {
      console.error(error);
    }
  };

  const handlePrint = () => {
    if (!selectedDock) return;
    getLabels({
      body: { values: [selectedDock.code], label_type: "location" },
      responseSetter: (res) => {
        if (res.success) printPDF(res.base64);
      },
    });
  };

  const handleSubmit = () => {
    if (!isAir && !dockId) {
      return popupNotification({
        dispatch: layoutDispatch,
        message: "Please select a dock.",
        status: "error",
      });
    }
    setIsLoading(true);
    receiveContainer({
      id: containerId,
      dockId: isAir ? undefined : dockId,
      responseSetter: (res) => {
        setIsLoading(false);
        if (res.success) {
          popupNotification({
            dispatch: layoutDispatch,
            message: "Container received successfully",
            status: "success",
          });
          onClose(true);
        } else {
          popupNotification({
            dispatch: layoutDispatch,
            message: res.message || "Failed to receive container",
            status: "error",
          });
        }
      },
    });
  };

  return (
    <Dialog
        open={open}
        onClose={() => !isLoading && onClose(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Receive Container</DialogTitle>
        <DialogContent>
          {isAir ? (
            <Typography>
              Items will be created on AIRSHIP. Do you want to receive this
              container?
            </Typography>
          ) : (
            <>
              <Typography gutterBottom>
                Select the dock this container will be received to. Items will be
                created on the selected dock.
              </Typography>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={8}>
                  <FormControl variant="outlined" fullWidth margin="dense">
                    <InputLabel id="dock-select-label">Dock</InputLabel>
                    <Select
                      labelId="dock-select-label"
                      label="Dock"
                      value={dockId}
                      onChange={(e) => setDockId(e.target.value)}
                    >
                      {docks.map((dock) => (
                        <MenuItem key={dock.id} value={dock.id}>
                          {dock.code}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={4}>
                  <Button
                    variant="outlined"
                    fullWidth
                    disabled={!selectedDock}
                    onClick={handlePrint}
                    startIcon={<Printer color="black" />}
                  >
                    Print
                  </Button>
                </Grid>
              </Grid>
              {docks.length === 0 && (
                <Typography color="error" variant="caption">
                  No available docks. Free up a dock or check dock setup.
                </Typography>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => onClose(false)}
            color="primary"
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            color="secondary"
            variant="contained"
            disabled={isLoading || (!isAir && !dockId)}
          >
            {isLoading ? <CircularProgress size={24} /> : "Confirm"}
          </Button>
        </DialogActions>
    </Dialog>
  );
}
